// Schaltergruppe: eine Auswahl aus wenigen benannten Zustaenden, als Pillen
// (ST-UX-001, G2b; APG "Button" mit aria-pressed).
//
// Jeder Knopf ist ein eigener Tabstopp, Leertaste und Enter schalten; kein
// Roving-Tabindex. Der Zustand "gedrueckt" traegt MEHRERE Merkmale, nie nur
// Farbe (WCAG 1.4.1): Flaeche brand-soft, 2 px Rand in der Marke (>= 3:1 gegen die
// Karte, 1.4.11) und ein Haken. brand-soft allein waere 1,15:1. Die Schrift
// wechselt ihr Gewicht NICHT: sonst springt die Pille beim Umschalten.
// Der Haken liegt im Innenabstand, nicht im Textfluss: der Text sitzt mittig,
// und die Pille ist in Ruhe und gedrueckt gleich breit.
// Nicht colorBrandBackground als Flaeche: dunkel haelt es gegen die Karte keine 3:1.

import type { ReactElement, Ref } from 'react';
import { makeStyles, mergeClasses, tokens } from '@fluentui/react-components';

export interface SchalterOption {
  readonly id: string;
  readonly label: string;
}

const useStyles = makeStyles({
  gruppe: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.5rem',
  },
  knopf: {
    appearance: 'none',
    boxSizing: 'border-box',
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    minHeight: '28px',
    padding: '0 calc(0.875rem + 7px)',
    // v4 chip: ungewaehlt gestrichelt und ohne Flaeche; der Rand traegt die
    // Feldgrenze (colorNeutralStrokeAccessible, >= 3:1, WCAG 1.4.11).
    border: `1px dashed ${tokens.colorNeutralStrokeAccessible}`,
    borderRadius: '999px',
    backgroundColor: 'transparent',
    color: tokens.colorNeutralForeground1,
    fontFamily: 'inherit',
    fontSize: tokens.fontSizeBase300,
    cursor: 'pointer',
    ':focus-visible': {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: '2px',
    },
    '&[aria-pressed="true"]': {
      // Der Rand wird 1 px dicker; der Innenabstand gibt es zurueck, die Pille springt nicht.
      border: `2px solid ${tokens.colorBrandStroke1}`,
      padding: '0 calc(0.875rem + 6px)',
      backgroundColor: tokens.colorBrandBackground2,
      color: tokens.colorBrandForeground2,
    },
    // Der Rand ist gedrueckt 1 px dicker; der Haken rueckt mit, damit er nicht wandert.
    '&[aria-pressed="true"] > svg': {
      insetInlineStart: 'calc(0.625rem - 8px)',
    },
  },
  // Der Haken ist immer da und nur in Ruhe unsichtbar; er liegt im linken
  // Innenabstand und verbraucht keine Breite.
  haken: {
    visibility: 'hidden',
    position: 'absolute',
    insetInlineStart: 'calc(0.625rem - 7px)',
    top: '50%',
    transform: 'translateY(-50%)',
  },
  hakenSichtbar: {
    visibility: 'visible',
  },
});

function Haken({ sichtbar }: { readonly sichtbar: boolean }): ReactElement {
  const own = useStyles();
  return (
    <svg className={mergeClasses(own.haken, sichtbar && own.hakenSichtbar)} viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="m5 12.5 4.5 4.5L19 7.5" />
    </svg>
  );
}

export function Schaltergruppe({
  label,
  optionen,
  gewaehlt,
  onWahl,
  className,
  gruppeRef,
}: {
  /** Name der Gruppe (aria-label), z. B. "Verlauf filtern". */
  readonly label: string;
  readonly optionen: readonly SchalterOption[];
  readonly gewaehlt: string;
  readonly onWahl: (id: string) => void;
  readonly className?: string | undefined;
  readonly gruppeRef?: Ref<HTMLDivElement> | undefined;
}): ReactElement {
  const own = useStyles();
  return (
    <div role="group" aria-label={label} className={mergeClasses(own.gruppe, className)} ref={gruppeRef}>
      {optionen.map((option) => (
        <button key={option.id} type="button" className={own.knopf} aria-pressed={gewaehlt === option.id} onClick={() => onWahl(option.id)}>
          <Haken sichtbar={gewaehlt === option.id} />
          {option.label}
        </button>
      ))}
    </div>
  );
}
